import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { syncNativeTheme } from '@/platform/native'
import { getJsonStorage, setStorage } from '@/platform/storage'

/** 主题模式 */
export type ThemeMode = 'light' | 'dark' | 'system'

/** 主题色板（变量名与 styles/main.scss 的 --theme-* 严格对齐） */
interface ThemeColors {
  primary: string
  onPrimary: string
  primaryRgb: string
  secondary: string
  accent: string
  background: string
  backgroundRgb: string
  backgroundGradient: string
  surface: string
  surfaceRgb: string
  card: string
  cardHover: string
  textPrimary: string
  textSecondary: string
  textTertiary: string
  textPlaceholder: string
  border: string
  borderLight: string
  borderStrong: string
  error: string
  warning: string
  success: string
}

// 暗色主题（与 main.scss :root 默认值一致）
const darkColors: ThemeColors = {
  primary: '#00d4ff',
  onPrimary: '#04141a',
  primaryRgb: '0, 212, 255',
  secondary: '#ff7a17',
  accent: '#a0c3ec',
  background: '#0a0a0a',
  backgroundRgb: '10, 10, 10',
  backgroundGradient: 'linear-gradient(180deg, #0a0a0a 0%, #131316 100%)',
  surface: '#1a1c20',
  surfaceRgb: '26, 28, 32',
  card: '#191919',
  cardHover: '#1f2023',
  textPrimary: '#fff',
  textSecondary: '#dadbdf',
  textTertiary: '#7d8187',
  textPlaceholder: '#5a5d63',
  border: '#212327',
  borderLight: 'rgb(255 255 255 / 6%)',
  borderStrong: 'rgb(255 255 255 / 25%)',
  error: '#ff5252',
  warning: '#ffc285',
  success: '#a0c3ec',
}

// 浅色主题（备选，当前 UI 组件主要为暗色优化，浅色为预留）
const lightColors: ThemeColors = {
  primary: '#00a8cc',
  onPrimary: '#fff',
  primaryRgb: '0, 168, 204',
  secondary: '#e85d04',
  accent: '#3b82f6',
  background: '#f7f8fa',
  backgroundRgb: '247, 248, 250',
  backgroundGradient: 'linear-gradient(180deg, #f7f8fa 0%, #eef0f3 100%)',
  surface: '#fff',
  surfaceRgb: '255, 255, 255',
  card: '#fff',
  cardHover: '#f2f3f5',
  textPrimary: '#18181b',
  textSecondary: '#52525b',
  textTertiary: '#a1a1aa',
  textPlaceholder: '#c4c4c8',
  border: '#e4e4e7',
  borderLight: 'rgb(24 24 27 / 8%)',
  borderStrong: 'rgb(24 24 27 / 24%)',
  error: '#ef4444',
  warning: '#f59e0b',
  success: '#10b981',
}

const STORAGE_KEY = 'theme-mode'

function resolveSystemDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return true
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

export const useThemeStore = defineStore('theme', () => {
  const themeMode = ref<ThemeMode>(getJsonStorage<ThemeMode>(STORAGE_KEY, 'dark'))
  let mediaListener: ((e: MediaQueryListEvent) => void) | null = null

  const isDark = computed(() => {
    if (themeMode.value === 'system') {
      return resolveSystemDark()
    }
    return themeMode.value === 'dark'
  })

  const currentColors = computed<ThemeColors>(() => {
    return isDark.value ? darkColors : lightColors
  })

  /** 将 ThemeColors 写入 CSS 变量（变量名与 main.scss :root 严格对齐） */
  function applyCSSVariables(colors: ThemeColors) {
    if (typeof document === 'undefined') {
      return
    }
    const root = document.documentElement
    root.style.setProperty('--theme-primary', colors.primary)
    root.style.setProperty('--theme-on-primary', colors.onPrimary)
    root.style.setProperty('--theme-primary-rgb', colors.primaryRgb)
    root.style.setProperty('--theme-secondary', colors.secondary)
    root.style.setProperty('--theme-accent', colors.accent)
    root.style.setProperty('--theme-background', colors.background)
    root.style.setProperty('--theme-background-rgb', colors.backgroundRgb)
    root.style.setProperty('--theme-background-gradient', colors.backgroundGradient)
    root.style.setProperty('--theme-surface', colors.surface)
    root.style.setProperty('--theme-surface-rgb', colors.surfaceRgb)
    root.style.setProperty('--theme-card', colors.card)
    root.style.setProperty('--theme-card-hover', colors.cardHover)
    root.style.setProperty('--theme-text-primary', colors.textPrimary)
    root.style.setProperty('--theme-text-secondary', colors.textSecondary)
    root.style.setProperty('--theme-text-tertiary', colors.textTertiary)
    root.style.setProperty('--theme-text-placeholder', colors.textPlaceholder)
    root.style.setProperty('--theme-border', colors.border)
    root.style.setProperty('--theme-border-light', colors.borderLight)
    root.style.setProperty('--theme-border-strong', colors.borderStrong)
    root.style.setProperty('--theme-error', colors.error)
    root.style.setProperty('--theme-warning', colors.warning)
    root.style.setProperty('--theme-success', colors.success)
    root.style.colorScheme = isDark.value ? 'dark' : 'light'
    void syncNativeTheme(isDark.value)
  }

  function syncMediaListener() {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return
    }
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    if (mediaListener) {
      mql.removeEventListener('change', mediaListener)
      mediaListener = null
    }
    if (themeMode.value === 'system') {
      mediaListener = () => applyCSSVariables(currentColors.value)
      mql.addEventListener('change', mediaListener)
    }
  }

  function setThemeMode(mode: ThemeMode) {
    themeMode.value = mode
    setStorage(STORAGE_KEY, mode)
    syncMediaListener()
    applyCSSVariables(currentColors.value)
  }

  /** 初始化主题，应在 App.vue onLaunch 时调用 */
  function initTheme() {
    syncMediaListener()
    applyCSSVariables(currentColors.value)
  }

  return {
    themeMode,
    isDark,
    currentColors,
    setThemeMode,
    initTheme,
  }
})
